// @ts-nocheck
import { useState } from 'react';
import { DataGrid, GridColumnMenuContainer, GridColumnMenuSortItem, GridColumnMenuFilterItem, getGridStringOperators } from '@mui/x-data-grid';
import { IconButton, Button, Box, MenuItem, Select, Typography } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import HospitalEditDialog from './HospitalEditDialog.jsx';
import HospitalDeleteDialog from './HospitalDeleteDialog.jsx';

function CustomColumnMenu(props) {
  const { hideMenu, colDef } = props;
  return (
    <GridColumnMenuContainer {...props}>
      <GridColumnMenuSortItem onClick={hideMenu} colDef={colDef} />
      <GridColumnMenuFilterItem onClick={hideMenu} colDef={colDef} />
    </GridColumnMenuContainer>
  );
}

const supportedStringFilterOperators = ['startsWith', 'equals']
  .map((operatorValue) => getGridStringOperators().find(({ value }) => value === operatorValue))
  .filter(Boolean);

export default function HospitalsDataGrid(props) {
  const {
    rows = [],
    loading = false,
    defaultPageSize = 10,
    pageSizeOptions = [10, 15, 25],
    page,
    pageSize,
    hasMore = false,
    filterModel,
    onFilterModelChange,
    onRowView,
    onRowUpdate,
    onRowAdd,
    onRowDelete,
  } = props;

  const safeRows = (rows || []).map((r, i) => ({ id: r.id ?? `row-${i}`, ...r }));

  const columns = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 2,
      minWidth: 180,
      filterOperators: supportedStringFilterOperators,
    },
    {
      field: 'contactEmail',
      headerName: 'Contact Email',
      flex: 1.5,
      minWidth: 220,
      filterOperators: supportedStringFilterOperators,
    },
    {
      field: 'totalUploads',
      headerName: 'Total Uploads',
      flex: 1,
      minWidth: 140,
      filterable: false,
    },
    {
      field: 'uploadsThisMonth',
      headerName: 'Monthly Uploads',
      flex: 1,
      minWidth: 170,
      filterable: false,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 140,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const hospital = params.row || {};
        return (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', height: '100%', justifyContent: 'center' }}>
            <IconButton
              size="small"
              sx={{ width: 28, height: 28, p: 0.25 }}
              aria-label={`view-${hospital.id}`}
              title={`View ${hospital.name || ''}`}
              onClick={() => typeof onRowView === 'function' && onRowView(hospital)}
            >
              <VisibilityIcon sx={{ fontSize: 16 }} />
            </IconButton>

            <IconButton
              size="small"
              sx={{ width: 28, height: 28, p: 0.25 }}
              aria-label={`edit-${hospital.id}`}
              title={`Edit ${hospital.name || ''}`}
              onClick={() => openEditDialog(hospital)}
            >
              <EditIcon sx={{ fontSize: 16 }} />
            </IconButton>

            <IconButton
              size="small"
              color="error"
              sx={{ width: 28, height: 28, p: 0.25 }}
              aria-label={`delete-${hospital.id}`}
              title={`Delete ${hospital.name || ''}`}
              onClick={() => handleDeleteClick(hospital)}
            >
              <DeleteIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </div>
        );
      },
    },
  ];

  const [editingHospital, setEditingHospital] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deletingHospital, setDeletingHospital] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [dialogError, setDialogError] = useState(null);
  const [saving, setSaving] = useState(false);

  const openEditDialog = (hospital) => {
    setEditingHospital(hospital);
    setDialogError(null);
    setDialogOpen(true);
  };

  const openAddDialog = () => {
    setEditingHospital(null);
    setDialogError(null);
    setDialogOpen(true);
  };

  const closeEditDialog = () => {
    setEditingHospital(null);
    setDialogOpen(false);
    setDialogError(null);
  };

  const handleDeleteClick = (hospital) => {
    setDeletingHospital(hospital);
    setConfirmOpen(true);
  };

  const handleCancelDelete = () => {
    setDeletingHospital(null);
    setConfirmOpen(false);
  };

  const handleConfirmDelete = () => {
    if (!deletingHospital) return;
    if (typeof onRowDelete === 'function') onRowDelete(deletingHospital);
    if (editingHospital && editingHospital.id === deletingHospital.id) closeEditDialog();
    setDeletingHospital(null);
    setConfirmOpen(false);
  };

  const handleSave = (updatedHospital) => {
    setDialogError(null);
    setSaving(true);
    const exists = updatedHospital.id && rows.some((r) => r.id === updatedHospital.id);
    let actionPromise;

    if (exists) {
      if (typeof onRowUpdate === 'function') actionPromise = onRowUpdate(updatedHospital);
    } else {
      if (typeof onRowAdd === 'function') {
        actionPromise = onRowAdd(updatedHospital);
      }
    }

    if (actionPromise && actionPromise.then) {
      actionPromise
        .then(() => {
          closeEditDialog();
        })
        .catch((err) => {
          setDialogError(err);
        })
        .finally(() => setSaving(false));
    } else {
      setSaving(false);
      closeEditDialog();
    }
  };

  return (
    <div
      style={{
        width: '90%',
        maxWidth: 1600,
        margin: '0 auto',
        paddingTop: '2.5vw',
        paddingBottom: '1.5vw',
      }}
    >
      <div style={{
        border: '1px solid #e0e0e0',
        borderRadius: 8,
        background: '#fff',
        overflow: 'hidden',
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingTop: 12,
          paddingBottom: 8,
          paddingLeft: 12,
          paddingRight: 12,
        }}>
          <div style={{ fontWeight: 400, fontSize: 32 }}>Hospitals</div>
          <div>
            <Button
              variant="contained"
              size="small"
              startIcon={<LocalHospitalIcon />}
              onClick={openAddDialog}
            >
              Add Hospital
            </Button>
          </div>
        </div>
        <DataGrid
          rows={safeRows}
          loading={loading}
          columns={columns}
          pagination={false}
          filterMode="server"
          filterModel={filterModel}
          onFilterModelChange={onFilterModelChange}
          slots={{ columnMenu: CustomColumnMenu }}
          density="compact"
          rowHeight={85}
          hideFooter
          sx={{
            border: 'none',
            borderRadius: 0,
            width: '100%',
            maxWidth: '100%',
            mt: 0,
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: 'transparent',
            },
            '& .MuiDataGrid-columnHeader': {
              backgroundColor: 'transparent',
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              fontWeight: 590,
              color: 'common.black',
              fontSize: 17,
            },
            '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
              outline: 'none',
            },
            '& .MuiDataGrid-row': {
              minHeight: 64,
              maxHeight: 64,
            },
          }}
          aria-label="Hospitals table"
        />
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 3,
            px: 2,
            py: 1,
            borderTop: '1px solid #e0e0e0',
            flexWrap: 'wrap',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Rows per page
            </Typography>
            <Select
              id="hospital-page-size"
              size="small"
              value={pageSize ?? defaultPageSize}
              onChange={(event) => {
                if (typeof props.onPageSizeChange === 'function') {
                  props.onPageSizeChange(Number(event.target.value));
                }
              }}
              disabled={loading}
              MenuProps={{ disableScrollLock: true }}
              sx={{
                minWidth: 72,
                '& .MuiSelect-select': {
                  py: 0.75,
                  pr: 3,
                  fontSize: 14,
                },
              }}
            >
              {pageSizeOptions.map((option) => (
                <MenuItem key={option} value={option}>{option}</MenuItem>
              ))}
            </Select>
          </Box>
          <Typography variant="body2" sx={{ color: 'text.secondary', minWidth: 56, textAlign: 'center' }}>
            {(page ?? 0) + 1}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
              size="small"
              onClick={() => typeof props.onPageChange === 'function' && props.onPageChange((page ?? 0) - 1)}
              disabled={loading || (page ?? 0) <= 0}
              aria-label="Previous page"
            >
              <KeyboardArrowLeftIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => typeof props.onPageChange === 'function' && props.onPageChange((page ?? 0) + 1)}
              disabled={loading || !hasMore}
              aria-label="Next page"
            >
              <KeyboardArrowRightIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      </div>

      <HospitalEditDialog
        open={dialogOpen}
        onClose={closeEditDialog}
        hospital={editingHospital}
        mode={editingHospital ? 'edit' : 'create'}
        onSave={handleSave}
        saving={saving}
        error={dialogError}
        onErrorClear={() => setDialogError(null)}
      />

      <HospitalDeleteDialog
        open={confirmOpen}
        hospital={deletingHospital}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
}
